import { Getter, Setter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { ICaptableDataModel } from '../../../data-models/captable2.data-model';
import { getCapTablesForCompany, getCaptableVersions } from '../../../services/queries/MaggieCapTableQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { fetchScenariosForCompany } from '../../../services/queries/MaggieScenarioQueries';
import {
  captablesByCompanyStateJ,
  captableVersionsByCompanyStateJ,
  primaryOrDefaultCaptableStateJ,
  selectedCaptableIdStateJ,
  selectedCaptableStateJ,
} from '../../../services/state/CapTableDataStateJ';
import { scenariosByCompanyIdStateJ } from '../../../services/state/ScenariosStateJ';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { resolveSelectedVersion } from './captable-utils';

/**
 *  BE does more than just creating/updating single captables,
 *  e.g. on a PUT request it will create a new captable with a different ID,
 * and might also make changes to other captables,
 * so the safest option is to invalidate all on every action;
 */

/** Enough of a mutation response to re-anchor the versions timeline on its (source, type) group. */
type SelectedCaptableRef = Pick<ICaptableDataModel, 'id' | 'source' | 'type'>;

interface IInvalidateCaptablesOptions {
  /** Select the ref's id outright even if the refetched timelines don't group it yet. */
  trustId?: boolean;
}

export function useInvalidateCaptables() {
  const { pushErrorToast } = useToastMessageState();
  const { asOfCapTables } = useFlags<MaggieFeatureFlags>();

  return useAtomCallback(
    useCallback(
      async (
        get: Getter,
        set: Setter,
        companyId: number,
        selectedCaptable?: SelectedCaptableRef,
        options?: IInvalidateCaptablesOptions
      ) => {
        try {
          const trustId = selectedCaptable != null && options?.trustId === true;
          if (asOfCapTables) {
            // Refetch the version timelines fresh (a mutation may create a new-id version) and the
            // current cap tables per source in a single company-wide call.
            const groups = await getCaptableVersions(companyId);
            await get(captableVersionsByCompanyStateJ(companyId));
            set(captableVersionsByCompanyStateJ(companyId), groups);

            const currentCaptables = await getCapTablesForCompany(companyId);
            await get(captablesByCompanyStateJ(companyId));
            set(captablesByCompanyStateJ(companyId), currentCaptables);

            if (trustId) {
              // Create and duplicate are the only mutations that can open a (source, type) timeline
              // the refetched groups don't carry yet, so their own id is authoritative rather than
              // re-anchored. (The versions endpoint does not lag the write path — see the spec.)
              await get(selectedCaptableIdStateJ(companyId));
              set(selectedCaptableIdStateJ(companyId), selectedCaptable.id);
            } else {
              // If the mutation's id isn't in the refetched timeline, it was merged into an existing
              // segment (e.g. FD-4176 classification edits). Re-anchor on (source, type) to land on
              // that segment's current version instead of reverting to the prior selection.
              const requested = selectedCaptable
                ? groups.flatMap((g) => g.versions).find((v) => v.id === selectedCaptable.id)
                : undefined;
              // Read the previous selection after the refetch so a write that landed while our
              // requests were in flight resolves against live state, not a stale snapshot.
              const previousSelected = selectedCaptable ? null : await get(selectedCaptableStateJ(companyId));
              const selected = selectedCaptable
                ? (requested ?? resolveSelectedVersion(groups, selectedCaptable))
                : resolveSelectedVersion(groups, previousSelected);
              await get(selectedCaptableIdStateJ(companyId));
              set(selectedCaptableIdStateJ(companyId), selected?.id ?? null);
            }
          } else {
            const captables = await getCapTablesForCompany(companyId);
            await get(captablesByCompanyStateJ(companyId));
            set(captablesByCompanyStateJ(companyId), captables);
            if (trustId) {
              await get(selectedCaptableIdStateJ(companyId));
              set(selectedCaptableIdStateJ(companyId), selectedCaptable.id);
            } else {
              const requested = selectedCaptable
                ? captables.find((c) => c.id === selectedCaptable.id)
                : undefined;
              const selected = requested ?? (await get(primaryOrDefaultCaptableStateJ(companyId)));
              await get(selectedCaptableIdStateJ(companyId));
              set(selectedCaptableIdStateJ(companyId), selected?.id ?? null);
            }
          }

          const scenarios = await fetchScenariosForCompany(companyId);
          await get(scenariosByCompanyIdStateJ(companyId));
          set(scenariosByCompanyIdStateJ(companyId), scenarios);
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Failed to refresh cap tables');
          pushErrorToast({ message });
        }
      },
      [asOfCapTables, pushErrorToast]
    )
  );
}
